<template>
  <main class="wallet-withdraw-success-page">
    <header class="withdraw-success-header">
      <button
        class="withdraw-success-header__back"
        type="button"
        :aria-label="t('home.title')"
        @click="goHome"
      >
        <AppSvgIcon :src="backIcon" size="17" />
      </button>
      <h1>{{ t("wallet.withdrawSubmittedHeader") }}</h1>
    </header>

    <section class="withdraw-success-content">
      <div class="withdraw-success-illustration" aria-hidden="true">
        <AppSvgIcon
          class="withdraw-success-illustration__glow"
          :src="glowImage"
          size="108.2"
        />
        <img :src="heroImage" alt="" />
      </div>

      <div class="withdraw-success-copy">
        <h2>{{ t("wallet.withdrawSubmittedTitle") }}</h2>
        <span class="withdraw-success-copy__status">
          {{ t("wallet.withdrawUnderReview") }}
        </span>
        <p>{{ t("wallet.withdrawReviewEta") }}</p>
      </div>
    </section>

    <section class="withdraw-success-notice" role="note">
      <img :src="noticeImage" alt="" />
      <div class="withdraw-success-notice__copy">
        <h2>{{ t("wallet.withdrawSubmitNotice") }}</h2>
        <p>{{ t("wallet.withdrawViewFlowHint") }}</p>
      </div>
    </section>

    <button
      class="withdraw-success-support"
      type="button"
      @click="goSupport"
    >
      <AppSvgIcon :src="supportIcon" size="20" />
      <span>{{ t("wallet.transferContactSupport") }}</span>
    </button>
  </main>
</template>

<script setup>
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import backIcon from "@/assets/images/SVG/icon/transfer-submitted-back.svg";
import glowImage from "@/assets/images/SVG/icon/transfer-submitted-glow.svg";
import supportIcon from "@/assets/images/SVG/icon/transfer-submitted-support.svg";
import heroImage from "@/assets/images/transfer/withdraw-review-hero.png";
import noticeImage from "@/assets/images/transfer/withdraw-review-note.png";

const { t } = useI18n();
const router = useRouter();

function goHome() {
  router.replace("/home/index");
}

function goSupport() {
  router.push("/mine/support-chat");
}
</script>

<style scoped lang="scss">
.wallet-withdraw-success-page {
  position: relative;
  width: 100%;
  max-width: 430px;
  min-height: var(--app-viewport-height);
  margin: 0 auto;
  padding-bottom: calc(env(safe-area-inset-bottom) + 76px);
  overflow-x: hidden;
  background: #fff;
  color: #090a1d;
}

.withdraw-success-header {
  position: absolute;
  z-index: 2;
  top: calc(env(safe-area-inset-top) + 14px);
  right: 0;
  left: 0;
  height: 24px;

  h1 {
    margin: 0;
    color: #000;
    font-size: 18px;
    font-weight: 600;
    line-height: 24px;
    text-align: center;
  }
}

.withdraw-success-header__back {
  position: absolute;
  top: 1px;
  left: 16px;
  display: flex;
  width: 24px;
  height: 24px;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 0;
  border: 0;
  background: transparent;
}

.withdraw-success-header__back :deep(.app-svg-icon) {
  width: 11px !important;
  height: 17px !important;
}

.withdraw-success-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: calc(env(safe-area-inset-top) + 74px);
}

.withdraw-success-illustration {
  position: relative;
  width: 100px;
  height: 100px;
  flex: 0 0 100px;
  overflow: hidden;

  > img {
    position: absolute;
    z-index: 1;
    display: block;
    top: -18.29%;
    left: -21.95%;
    width: 145.12%;
    max-width: none;
    height: 145.12%;
    object-fit: cover;
  }
}

.withdraw-success-illustration__glow {
  position: absolute;
  z-index: 0;
  top: 90px;
  left: 50%;
  width: 108.2px !important;
  height: 30.2px !important;
  transform: translateX(-50%);
  pointer-events: none;
}

.withdraw-success-copy {
  display: flex;
  width: min(301px, calc(100% - 32px));
  flex-direction: column;
  align-items: center;
  margin-top: 24px;
  text-align: center;

  h2 {
    margin: 0;
    color: #000;
    font-size: 20px;
    font-weight: 600;
    line-height: 20px;
  }

  p {
    margin: 12px 0 0;
    color: #999;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
  }
}

.withdraw-success-copy__status {
  margin-top: 16px;
  padding: 4px 6px;
  border-radius: 4px;
  background: rgba(116, 66, 232, 0.12);
  color: #7442e8;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
}

.withdraw-success-notice {
  display: flex;
  width: min(343px, calc(100% - 32px));
  min-height: 90px;
  align-items: center;
  gap: 10px;
  margin: 24px auto 0;
  padding: 14px 16px;
  border-radius: 16px;
  background: #f7f3ff;

  > img {
    display: block;
    width: 42px;
    height: 42px;
    flex: 0 0 42px;
    object-fit: contain;
  }
}

.withdraw-success-notice__copy {
  min-width: 0;

  h2 {
    margin: 0;
    color: #090a1d;
    font-size: 16px;
    font-weight: 500;
    line-height: 22px;
  }

  p {
    margin: 8px 0 0;
    color: #747b88;
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
  }
}

.withdraw-success-support {
  position: fixed;
  z-index: 3;
  bottom: calc(env(safe-area-inset-bottom) + 16px);
  left: 50%;
  display: flex;
  width: min(343px, calc(100% - 32px));
  height: 44px;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 0 12px;
  border: 0;
  border-radius: 30px;
  background: #f7f3ff;
  color: #7442e8;
  font-size: 16px;
  font-weight: 500;
  transform: translateX(-50%);
}

.withdraw-success-support :deep(.app-svg-icon) {
  width: 20px;
  height: 20px;
}
</style>
